import { useCallback, useEffect, useMemo, useState } from 'react';
import PolyHoleScreen from './screens/PolyHoleScreen';
import SwipePager from './components/SwipePager';
import { loadCourse, holeHasGeometry } from './lib/courses';
import { useSettings } from './lib/settings';
import { watchSurface } from './lib/surface';
import type { Course, Hole } from './lib/types';

/**
 * Eighteen screens. One hole each. Two buttons on every one of them.
 *
 * Built as eighteen real panels rather than one panel with a changing hole, so
 * that "screen 7" is a thing that exists and can be linked to, screenshotted,
 * and swiped between with the neighbours already rendered. `?hole=7` lands on
 * it; the arrow keys, a touch swipe and the two buttons all move the same
 * index, so the surface behaves the same whether it is being driven by a
 * thumb, a trackpad, a keyboard or a head-unit rotary.
 *
 * A course with fewer than eighteen mapped holes still gets eighteen screens:
 * the missing ones say so rather than silently renumbering, because a golfer
 * counting tee boxes will notice a gap long before they notice a footnote.
 */

/** Placeholder for a hole the course data has not mapped. */
function Unmapped({ n, total, onPrev, onNext }: {
  n: number; total: number; onPrev: () => void; onNext: () => void;
}) {
  return (
    <div className="poly-hole">
      <header className="ph-head">
        <span className="ph-no">{n}</span>
        <span className="ph-of">of {total}</span>
        <span className="ph-meta">NOT MAPPED</span>
      </header>
      <div className="ph-figure"><div className="hs-empty">Hole {n} is not mapped yet</div></div>
      <footer className="ph-buttons">
        <button className="ph-btn" onClick={onPrev} aria-label="Previous hole"><span aria-hidden="true">‹</span></button>
        <button className="ph-btn" onClick={onNext} aria-label="Next hole"><span aria-hidden="true">›</span></button>
      </footer>
    </div>
  );
}

export default function EighteenApp() {
  const q = new URLSearchParams(location.search);
  const { settings } = useSettings();
  const [course, setCourse] = useState<Course | null>(null);
  const [failed, setFailed] = useState<string | null>(null);

  const count = Math.max(1, Math.min(36, Number(q.get('holes')) || 18));
  const start = Math.min(count, Math.max(1, Number(q.get('hole')) || 1));
  const [index, setIndex] = useState(start - 1);

  // The stylesheet does the device work; this only tells it which device.
  useEffect(() => watchSurface(), []);

  const courseId = q.get('course') ?? settings.courseId ?? 'way/43527178';

  useEffect(() => {
    let alive = true;
    loadCourse(courseId)
      .then((r) => { if (alive) setCourse(r.course); })
      .catch((e) => { if (alive) setFailed(String(e)); });
    return () => { alive = false; };
  }, [courseId]);

  // Deep-linkable without a router: the URL follows the pager so a screenshot
  // of screen 12 is a link to screen 12.
  useEffect(() => {
    const u = new URL(location.href);
    u.searchParams.set('hole', String(index + 1));
    history.replaceState(null, '', u.toString());
  }, [index]);

  const teeColour = settings.teeColour ?? course?.teeColours?.[0] ?? 'White';
  const unit = settings.unit ?? 'm';

  const go = useCallback(
    (delta: number) => setIndex((i) => (i + delta + count) % count),
    [count],
  );

  const panels = useMemo(() => {
    const byNumber = new Map<number, Hole>();
    for (const h of course?.holes ?? []) if (holeHasGeometry(h)) byNumber.set(h.number, h);
    return Array.from({ length: count }, (_, i) => {
      const n = i + 1;
      const hole = byNumber.get(n);
      return {
        key: `hole-${n}`,
        node: hole ? (
          <PolyHoleScreen
            hole={hole}
            teeColour={teeColour}
            unit={unit}
            total={count}
            onPrev={() => go(-1)}
            onNext={() => go(1)}
          />
        ) : (
          <Unmapped n={n} total={count} onPrev={() => go(-1)} onNext={() => go(1)} />
        ),
      };
    });
  }, [course, count, teeColour, unit, go]);

  if (failed) return <div className="poly-host"><div className="hs-empty">{failed}</div></div>;
  if (!course) return <div className="poly-host"><div className="hs-empty">Loading…</div></div>;

  return (
    <div className="poly-host">
      <SwipePager panels={panels} index={index} onIndex={setIndex} />
    </div>
  );
}
